const WHATSAPP_BASE_URL = 'https://wa.me';

/**
 * Builds a `wa.me` deep link with a URL-encoded pre-filled message.
 *
 * Security:
 * - Phone number comes from the build-time env var `VITE_WHATSAPP_NUMBER`
 *   (never from user input). Sanitized with `replace(/\D/g, '')` so accidental
 *   `+`, spaces or dashes in `.env` cannot corrupt the URL (T-03-02 mitigation).
 * - Message is passed through `encodeURIComponent` to prevent URL injection
 *   (T-03-01 mitigation). Today the message is a build-time constant from
 *   `landingCopy.whatsapp.defaultMessage`, but the defense is permanent.
 * - `wa.me` itself routes to the WhatsApp app on mobile and to
 *   web.whatsapp.com on desktop (PRD §5.3).
 *
 * Caller contract (enforced by Plan 06 acceptance criteria):
 * - When rendering the resulting URL inside an `<a>`, you MUST set
 *   `target="_blank"` AND `rel="noopener noreferrer"` to prevent reverse
 *   tabnabbing (T-03-03 mitigation).
 *
 * Degrade-gracefully behavior:
 * - When `VITE_WHATSAPP_NUMBER` is empty/undefined (e.g. Vercel preview before
 *   Phase 6 wiring), returns `https://wa.me/?text=...` — a harmless link that
 *   simply opens WhatsApp without a destination, never throws.
 *
 * @param message - PT-BR plaintext message to pre-fill (from
 *                  `landingCopy.whatsapp.defaultMessage`).
 * @param sourceId - Click origin identifier (e.g. `'hero-cta'`). Accepted for
 *                   forward-compat with analytics (Phase 6 / CONV-03) but NOT
 *                   appended to the URL today — `wa.me` silently drops unknown
 *                   query params, and adding it would give zero behavioral
 *                   value. Origin will be dispatched via GA4 event from the
 *                   click handler in Phase 6.
 * @returns Absolute URL like
 *          `https://wa.me/5551999999999?text=Ol%C3%A1%20...`
 */
export function whatsappLink(message: string, sourceId: string): string {
  // `sourceId` is intentionally unused in the URL today. Referenced here to
  // satisfy `@typescript-eslint/no-unused-vars` without weakening the public
  // signature that Phase 6 analytics will consume.
  void sourceId;

  const rawNumber = import.meta.env.VITE_WHATSAPP_NUMBER ?? '';
  const number = rawNumber.replace(/\D/g, '');
  const text = encodeURIComponent(message);

  return `${WHATSAPP_BASE_URL}/${number}?text=${text}`;
}
